import React, { useEffect } from "react";
import { useUserForm } from "../hooks/useUserForm";
import { Form, Formik, useFormikContext } from "formik";
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  TextField,
} from "@mui/material";
import { getEntityId, mergeSelectedIntoOptions } from "utils/entityLookup";

function UserForm({
  editMode,
  currentUser,
  setOpen,
  handleClose,
  currentUserName = { firstName: "", lastName: "" },
  setNewUserId = () => {},
}) {
  const {
    validationSchema,
    initialValues,
    handleSubmit,
    roles,
    divisionSearchResults,
    resolvedDivisions,
    resolvedLevels,
    setSearchDivisionTerm,
    setValuesLocation,
    levelsForLocation,
    locationsAll,
  } = useUserForm({
    editMode,
    currentUser,
    setOpen,
    currentUserName,
    setNewUserId,
  });

  // New component to handle side-effects
  const LocationWatcher = ({ setValuesLocation }) => {
    const { values } = useFormikContext();

    useEffect(() => {
      if (values.locations && values.locations.length > 0) {
        setValuesLocation(values.locations[0]);
      }
    }, [values.locations, setValuesLocation]);

    return null; // This component doesn't render anything
  };

  const RoleWatcher = ({ setFieldValue }) => {
    const { values } = useFormikContext();

    useEffect(() => {
      const hasHeadOfYear = values.roles?.some(
        (role) => role.name === "Head of Year",
      );

      if (!hasHeadOfYear && values.levels && values.levels.length > 0) {
        setFieldValue("levels", []);
      }
    }, [values.roles, values.levels, setFieldValue]);

    return null;
  };

  const LevelsResolver = ({ availableLevels }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (!Array.isArray(values.levels) || values.levels.length === 0) return;
      if (!Array.isArray(availableLevels) || availableLevels.length === 0)
        return;

      const hasIds = values.levels.some((lvl) => typeof lvl === "string");
      if (!hasIds) return;

      const resolved = values.levels
        .map((lvl) => {
          if (typeof lvl !== "string") return lvl;
          return (
            availableLevels.find(
              (opt) => getEntityId(opt) === getEntityId(lvl),
            ) || null
          );
        })
        .filter(Boolean);

      // Only update if we resolved at least one id to an object.
      if (resolved.length > 0) {
        setFieldValue("levels", resolved, false);
      }
    }, [values.levels, availableLevels, setFieldValue]);

    return null;
  };

  const LocationsResolver = ({ locationsAll }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (!Array.isArray(values.locations) || values.locations.length === 0)
        return;
      if (!Array.isArray(locationsAll) || locationsAll.length === 0) return;

      const hasIds = values.locations.some((loc) => typeof loc === "string");
      if (!hasIds) return;

      const resolved = values.locations
        .map((loc) => {
          if (typeof loc !== "string") return loc;
          return (
            locationsAll.find((opt) => getEntityId(opt) === getEntityId(loc)) ||
            null
          );
        })
        .filter(Boolean);

      if (resolved.length > 0) {
        setFieldValue("locations", resolved, false);
      }
    }, [values.locations, locationsAll, setFieldValue]);

    return null;
  };

  const DivisionsResolver = ({ availableDivisions }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
      if (!Array.isArray(values.divisions) || values.divisions.length === 0)
        return;
      if (!Array.isArray(availableDivisions) || availableDivisions.length === 0)
        return;

      const hasIds = values.divisions.some((div) => typeof div === "string");
      if (!hasIds) return;

      const resolved = values.divisions
        .map((div) => {
          if (typeof div !== "string") return div;
          return (
            availableDivisions.find(
              (opt) => getEntityId(opt) === getEntityId(div),
            ) || null
          );
        })
        .filter(Boolean);

      if (resolved.length > 0) {
        setFieldValue("divisions", resolved, false);
      }
    }, [values.divisions, availableDivisions, setFieldValue]);

    return null;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isSubmitting,
        getFieldProps,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => {
        const baseDivisionOptions = [
          ...(divisionSearchResults || []),
          ...(resolvedDivisions || []),
        ];

        const divisionOptions = mergeSelectedIntoOptions(
          baseDivisionOptions,
          values.divisions || [],
        );

        const baseLevelOptions = [
          ...(levelsForLocation || []),
          ...(resolvedLevels || []),
        ];

        const levelOptions = mergeSelectedIntoOptions(
          baseLevelOptions,
          values.levels || [],
        );

        return (
          <Form onSubmit={handleSubmit}>
            {/* This watches for changes in the location */}
            <LocationWatcher setValuesLocation={setValuesLocation} />
            <RoleWatcher setFieldValue={setFieldValue} />
            <LevelsResolver availableLevels={levelOptions} />
            <LocationsResolver locationsAll={locationsAll} />
            <DivisionsResolver availableDivisions={divisionOptions} />
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label="First Name"
                variant="outlined"
                margin="normal"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                {...getFieldProps("firstName")}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />

              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label="Last Name"
                variant="outlined"
                margin="normal"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                {...getFieldProps("lastName")}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />

              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                variant="outlined"
                margin="normal"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                {...getFieldProps("email")}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />

              <Autocomplete
                multiple
                options={divisionOptions}
                getOptionLabel={(option) =>
                  option && option.name && option.location
                    ? `${option.name} - ${option.location.name}`
                    : option?.name || ""
                }
                noOptionsText="No departments found"
                value={values.divisions || []}
                isOptionEqualToValue={(option, value) =>
                  getEntityId(option) === getEntityId(value)
                }
                onInputChange={(event, newInputValue) => {
                  setSearchDivisionTerm(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setFieldValue("divisions", newValue || []);
                  // auto-sync locations from selected divisions
                  const derivedLocations =
                    newValue
                      ?.map((d) => (typeof d === "object" ? d.location : null))
                      .filter(Boolean) || [];
                  setFieldValue("locations", derivedLocations);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departments"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.divisions && Boolean(errors.divisions)}
                    helperText={touched.divisions && errors.divisions}
                  />
                )}
              />

              <Autocomplete
                multiple
                options={locationsAll || []}
                getOptionLabel={(option) => option?.name || ""}
                noOptionsText="No locations found"
                value={values.locations || []}
                isOptionEqualToValue={(option, value) =>
                  getEntityId(option) === getEntityId(value)
                }
                onChange={(event, newValue) => {
                  setFieldValue("locations", newValue || []);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Schools"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.locations && Boolean(errors.locations)}
                    helperText={touched.locations && errors.locations}
                  />
                )}
              />

              <Autocomplete
                // map roles to get the name of the role
                multiple
                options={roles}
                getOptionLabel={(option) => option.name}
                fullWidth
                noOptionsText="No roles available"
                value={values.roles || []}
                isOptionEqualToValue={(option, value) =>
                  getEntityId(option) === getEntityId(value)
                }
                onChange={(event, newValue) => {
                  setFieldValue("roles", newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Role"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.roles && Boolean(errors.roles)}
                    helperText={touched.roles && errors.roles}
                  />
                )}
              />

              {/* Conditionally render the Year field if "Year Head Admin" is selected */}
              {values.roles.some((role) => role.name === "Head of Year") && (
                <Autocomplete
                  multiple
                  options={levelOptions}
                  getOptionLabel={(option) => option?.name || ""}
                  // noOptionsText="No year found"
                  value={values.levels || []}
                  isOptionEqualToValue={(option, value) =>
                    getEntityId(option) === getEntityId(value)
                  }
                  onChange={(event, newValue) => {
                    setFieldValue("levels", newValue || []);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Year(s)"
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      error={touched.levels && Boolean(errors.levels)}
                      helperText={touched.levels && errors.levels}
                    />
                  )}
                />
              )}

              <DialogActions>
                <Button onClick={handleClose} color="primary">
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {editMode ? "Update" : "Create"}
                </Button>
              </DialogActions>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default UserForm;
